import React, { useId } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  AreaChart,
  Area
} from 'recharts';
import styles from './HTSPillarsInfographic.module.css';

const HTS_PILLARS_DATA = {
  humanFactors: [
    { name: 'Distracted Driving', score: 35 },
    { name: 'Speeding Factors', score: 28 },
    { name: 'Impaired Driving', score: 22 },
    { name: 'Fatigue/Drowsiness', score: 10 },
    { name: 'Rule Disregard', score: 5 }
  ],
  safetyTech: [
    { year: '1990', fatalities: 2.1 },
    { year: '2000', fatalities: 1.5 },
    { year: '2010', fatalities: 1.1 },
    { year: '2020', fatalities: 1.3 },
    { year: '2025', fatalities: 1.0 }
  ],
  roadwayImpact: [
    { subject: 'Wet Conditions', A: 7, fullMark: 10 },
    { subject: 'Night Visibility', A: 8.5, fullMark: 10 },
    { subject: 'Curved Roads', A: 6, fullMark: 10 },
    { subject: 'Intersections', A: 9, fullMark: 10 },
    { subject: 'Snow/Ice', A: 8, fullMark: 10 }
  ]
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const HTSPillarsInfographic: React.FC = () => {
  const gradientId = useId();
  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <motion.h2 {...fadeInUp}>HTS: Highway Transportation System</motion.h2>
        <motion.p {...fadeInUp} transition={{ delay: 0.2 }}>
          Understanding the complex interaction between{' '}
          <strong className={styles.pillarPeople}>People</strong>,{' '}
          <strong className={styles.pillarVehicles}>Vehicles</strong>, and{' '}
          <strong className={styles.pillarRoadways}>Roadways</strong>.
        </motion.p>
      </header>

      {/* Section: The Triangle Relationship */}
      <section className={styles.triangleSection}>
        <div className={styles.header}>
          <h3>The Integrated Triangle</h3>
          <p>
            The HTS is not just a list of parts; it is a system of relationships. Each pillar constantly interacts with the others. When all three are in harmony, the system is safe.
          </p>
        </div>

        <div className={styles.triangleContainer}>
          {/* Connections */}
          <div className={`${styles.connector} ${styles.conBottom}`}></div>
          <div className={`${styles.connector} ${styles.conLeft}`}></div>
          <div className={`${styles.connector} ${styles.conRight}`}></div>

          {/* Pillar: People */}
          <motion.div 
            className={`${styles.pillarCard} ${styles.pillarTop}`}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <span className={styles.icon}>👤</span>
            <h3 className={`${styles.pillarTitle} ${styles.pillarPeople}`}>People</h3>
          </motion.div>

          {/* Pillar: Vehicles */}
          <motion.div 
            className={`${styles.pillarCard} ${styles.pillarLeft}`}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <span className={styles.icon}>🚗</span>
            <h3 className={`${styles.pillarTitle} ${styles.pillarVehicles}`}>Vehicles</h3>
          </motion.div>

          {/* Pillar: Roadways */}
          <motion.div 
            className={`${styles.pillarCard} ${styles.pillarRight}`}
            whileHover={{ scale: 1.05, y: -5 }}
          >
            <span className={styles.icon}>🛣️</span>
            <h3 className={`${styles.pillarTitle} ${styles.pillarRoadways}`}>Roadways</h3>
          </motion.div>
        </div>
      </section>

      {/* Pillar 1: People */}
      <section className={styles.pillarGrid}>
        <motion.div className={styles.content} {...fadeInUp}>
          <div className={`${styles.pillarBadge} ${styles.badgePeople}`}>Pillar One</div>
          <h2 className={styles.sectionTitle}>The Human Factor</h2>
          <p className={styles.sectionText}>
            People are the most unpredictable and vulnerable component of the HTS. Because humans are prone to error, distraction, and fatigue, this pillar accounts for the vast majority of HTS failures.
          </p>
          <div className={styles.statsGrid}>
            <div className={`${styles.statCard} ${styles.statOrange}`}>
              <span className={styles.statValue}>90%</span>
              <p className={styles.statLabel}>Of crashes involve human error.</p>
            </div>
            <div className={`${styles.statCard} ${styles.statOrange}`}>
              <span className={styles.statValue}>3-5s</span>
              <p className={styles.statLabel}>Average response time for alert drivers.</p>
            </div>
          </div>
        </motion.div>
        <motion.div className={styles.chart} {...fadeInUp}>
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Primary Factors in Human-Related Crashes</h3>
            <div className={styles.chartWrapper}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={HTS_PILLARS_DATA.humanFactors} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    tick={{ fontSize: 10, fontWeight: 500 }} 
                    width={100}
                  />
                  <Tooltip 
                    cursor={{ fill: 'rgba(249, 115, 22, 0.1)' }}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <Bar dataKey="score" fill="#f97316" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className={styles.chartCaption}>Data represents relative impact on total roadway fatalities.</p>
          </div>
        </motion.div>
      </section>

      {/* Pillar 2: Vehicles */}
      <section className={styles.pillarGrid}>
        <motion.div className={styles.content} {...fadeInUp}>
          <div className={`${styles.pillarBadge} ${styles.badgeVehicles}`}>Pillar Two</div>
          <h2 className={styles.sectionTitle}>Vehicle Engineering</h2>
          <p className={styles.sectionText}>
            Modern HTS relies on vehicle technology (like automatic braking) to compensate for human error. Each vehicle has different handling characteristics and safety requirements.
          </p>
          <ul className={styles.featureList}>
            <li className={styles.featureItem}>
              <span className={styles.featureIcon}>✔</span>
              <div className={styles.featureContent}>
                <strong>Performance</strong>
                <span>Handling, braking, and acceleration capabilities.</span>
              </div>
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureIcon}>✔</span>
              <div className={styles.featureContent}>
                <strong>Protection</strong>
                <span>Crumple zones and restraint systems.</span>
              </div>
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureIcon}>✔</span>
              <div className={styles.featureContent}>
                <strong>Maintenance</strong>
                <span>Ensuring tires, lights, and fluid levels are operational.</span>
              </div>
            </li>
          </ul>
        </motion.div>
        <motion.div className={styles.chart} {...fadeInUp}>
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Fatality Rate vs. Safety Technology Adoption</h3>
            <div className={styles.chartWrapper}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={HTS_PILLARS_DATA.safetyTech}>
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} domain={[0, 3]} />
                  <Tooltip />
                  <Area type="monotone" dataKey="fatalities" stroke="#06b6d4" fillOpacity={1} fill={`url(#${gradientId})`} strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className={styles.chartCaption}>HTS Fatalities per 100M Miles over time.</p>
          </div>
        </motion.div>
      </section>

      {/* Pillar 3: Roadways */}
      <section className={styles.pillarGrid}>
        <motion.div className={styles.content} {...fadeInUp}>
          <div className={`${styles.pillarBadge} ${styles.badgeRoadways}`}>Pillar Three</div>
          <h2 className={styles.sectionTitle}>Environment & Infrastructure</h2>
          <p className={styles.sectionText}>
            The roadway is the physical stage where the system operates. It includes everything from surface material to signs, signals, and lighting.
          </p>
        </motion.div>
        <motion.div className={styles.chart} {...fadeInUp}>
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Environmental Impact Comparison</h3>
            <div className={styles.chartWrapper}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={HTS_PILLARS_DATA.roadwayImpact}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <PolarRadiusAxis angle={30} domain={[0, 10]} />
                  <Radar
                    name="Risk Multiplier"
                    dataKey="A"
                    stroke="#6366f1"
                    fill="#6366f1"
                    fillOpacity={0.5}
                  />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <p className={styles.chartCaption}>Comparing how conditions affect overall safety scores.</p>
          </div>
        </motion.div>
      </section>

      {/* Footer Callout */}
      <motion.div {...fadeInUp} className={styles.footerCallout}>
        <div className={styles.callout}>
          <strong className={styles.calloutTitle}>Key Infrastructure Goals:</strong>
          <p className={styles.calloutText}>
            "Engineering roads to be 'forgiving'—ensuring that if a driver makes a mistake, the road helps them recover rather than causing a crash."
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default HTSPillarsInfographic;
